/**
 * Page code fetched ahead of the tap (Bobbie, 2026-10-26, on what is slow:
 * "just loading pages").
 *
 * Every page is its own lazy chunk (lib/lazyRoute.ts), so the first visit to a
 * page waits on its code before it can even ask for its data. Measured on the
 * whole-app harness, production build, a phone profile (Slow 4G, CPU 4x),
 * signed in as a family: the first open of /messages took 3.5 s to settle and
 * pulled 48 files (436 KB); the second open, with the code already loaded, took
 * 1.0 s. Calendar 1.9 s then 0.6 s, Tasks 1.6 s then 0.7 s. Most of a first
 * page load is code, and every release changes the file names, so on a phone
 * every page is a first visit again after each deploy.
 *
 * So once a page has settled, this fetches the code for the pages its links
 * lead to, one at a time, and a finger landing on a link fetches that page's
 * code on the spot (the gap between finger down and the click is a real
 * head start on a phone). Only code: no data is read ahead, so nothing here
 * can show anything older than the page would have shown anyway.
 *
 * It stands down on Low Data Mode / Data Saver, on a 2G connection, while an
 * upload is still sending (uploads were her other speed complaint; nothing
 * here may compete with one), and while the tab is hidden. Every fetch goes
 * through preloadChunk(), which never reloads the page on a failure.
 */
import { createRoutesFromChildren, matchRoutes, type RouteObject } from 'react-router-dom';
import { isValidElement, type ReactElement, type ReactNode } from 'react';
import { uploadInFlight } from './unsavedWork.ts';

/** What a route component may offer: lazyRoute() pages, and the wrappers that pick one. */
export interface WarmablePage {
  preload?: (context: WarmContext) => Promise<boolean>;
}

export interface WarmContext {
  /** A signed-in dancer session (not a Jr Staff one): the wrappers pick the dancer page. */
  dancer: boolean;
}

/**
 * A page counts as settled once its links and its "Loading..." lines have held
 * still this long (checked every WARM_POLL_MS), so its own data goes first.
 */
export const WARM_QUIET_MS = 1500;
export const WARM_POLL_MS = 500;
/** How long a page is watched for links after it opens. */
export const WARM_WATCH_MS = 30000;
/** At most this many pages warmed per page visited, in link order. */
export const WARM_PER_PAGE = 10;
/** Links read per page: a roster of 300 dancer links is one page, not 300 lookups. */
export const WARM_LINKS_READ = 60;

interface ConnectionLike {
  saveData?: boolean;
  effectiveType?: string;
}

/** navigator, with the Network Information API that Safari does not ship. */
export type NavigatorWithConnection = Navigator & { connection?: ConnectionLike };

/**
 * Data Saver or a 2G connection: a block that will not lift while this page
 * is open, so a held queue is dropped rather than polled for (#1465 refute).
 */
export function warmingBlockedForGood(nav: { connection?: ConnectionLike } | undefined): boolean {
  const connection = nav?.connection;
  if (connection?.saveData) return true;
  return connection?.effectiveType === '2g' || connection?.effectiveType === 'slow-2g';
}

/** Whether background warming may run right now. A tap's own warm ignores this. */
export function warmingAllowed(
  nav: { connection?: ConnectionLike; onLine?: boolean } | undefined,
  doc: { hidden?: boolean } | undefined,
  uploading: boolean = uploadInFlight(),
): boolean {
  if (uploading) return false;
  if (doc?.hidden) return false;
  // Offline, every fetch fails, and the browser keeps a failed module failed
  // for the life of the tab, so the later tap would reload the Hub instead of
  // opening the page (#1465 final review, proven in Chromium 141). Held, and
  // picked back up once the phone is online again.
  if (nav && (nav as { onLine?: boolean }).onLine === false) return false;
  return !warmingBlockedForGood(nav);
}

/**
 * True while the page is still showing a "Loading..." line (StudioLoading, or a
 * section's own). Warming waits for it, so the page's own data goes first.
 */
export function pageStillLoading(root: ParentNode): boolean {
  return Array.from(root.querySelectorAll('[role="status"]')).some((node) =>
    /loading/i.test(node.textContent ?? ''),
  );
}


/**
 * The in-app paths the links under `root` lead to, in page order, each once.
 * Footer links (About, Privacy, Terms) are left out: almost nobody taps them.
 */
export function linkedPaths(root: ParentNode, origin: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const anchor of Array.from(root.querySelectorAll('a[href]'))) {
    if (anchor.closest('footer')) continue;
    const path = inAppPath(anchor.getAttribute('href'), origin);
    if (path && !seen.has(path)) {
      seen.add(path);
      out.push(path);
    }
  }
  return out;
}

/** The pathname of an href that stays inside the Hub, or null. */
export function inAppPath(href: string | null, origin: string): string | null {
  if (!href || href.startsWith('#')) return null;
  try {
    const url = new URL(href, origin);
    if (url.origin !== origin) return null;
    return url.pathname;
  } catch {
    return null;
  }
}

/**
 * The page components a path opens, from the same <Route> tree the app
 * renders. A <Navigate> with a plain `to` is followed one step (the old
 * /mentions and /newsletters addresses), and a gate wrapping one page
 * (ShoutoutWallGate) is looked through.
 */
export function pagesForPath(routes: RouteObject[], path: string, depth = 0): WarmablePage[] {
  const matches = matchRoutes(routes, path);
  if (!matches) return [];
  const element = matches[matches.length - 1].route.element;
  if (!isValidElement(element)) return [];
  const own = warmableType(element);
  if (own) return [own];
  const props = element.props as { to?: unknown; children?: ReactNode };
  if (depth === 0 && typeof props.to === 'string') {
    const next = inAppPath(props.to, 'http://hub.invalid');
    return next ? pagesForPath(routes, next, depth + 1) : [];
  }
  if (isValidElement(props.children)) {
    const inner = warmableType(props.children as ReactElement);
    if (inner) return [inner];
  }
  return [];
}

function warmableType(element: ReactElement): WarmablePage | null {
  const type = element.type as unknown as WarmablePage | string;
  if (typeof type === 'string' || !type) return null;
  return typeof type.preload === 'function' ? type : null;
}

/** Turns <Route> children into the route objects matchRoutes() reads. */
export function routeObjectsFrom(children: ReactNode): RouteObject[] {
  return createRoutesFromChildren(children);
}

/**
 * One per tab. Remembers which pages it has already fetched, so a page is
 * fetched at most once however many links lead to it, and runs one fetch at a
 * time so a phone's connection is never flooded.
 */
export class RouteWarmer {
  private readonly done = new WeakSet<WarmablePage>();
  private queue: WarmablePage[] = [];
  private running = false;
  private stopped = false;

  private readonly routes: RouteObject[];
  private readonly context: () => WarmContext;
  private readonly allowed: () => boolean;
  private readonly blockedForGood: () => boolean;

  // Plain fields, not constructor parameter properties: node:test loads this
  // file with --experimental-strip-types, which refuses those.
  constructor(
    routes: RouteObject[],
    context: () => WarmContext,
    allowed: () => boolean,
    blockedForGood: () => boolean = () => false,
  ) {
    this.routes = routes;
    this.context = context;
    this.allowed = allowed;
    this.blockedForGood = blockedForGood;
  }

  /** Background warm for the links on a page that has settled. */
  warmPaths(paths: string[]): void {
    this.queue = [];
    if (this.stopped) return;
    let added = 0;
    for (const path of paths.slice(0, WARM_LINKS_READ)) {
      if (added >= WARM_PER_PAGE) break;
      for (const page of pagesForPath(this.routes, path)) {
        if (this.done.has(page) || this.queue.includes(page)) continue;
        this.queue.push(page);
        added += 1;
      }
    }
    void this.drain();
  }

  /** A finger is on a link: fetch that page now, ahead of anything queued. */
  warmNow(path: string): void {
    for (const page of pagesForPath(this.routes, path)) {
      if (this.done.has(page)) continue;
      this.done.add(page);
      void page.preload?.(this.context());
    }
  }

  /** Forget the queue (the person moved to another page). */
  cancel(): void {
    this.queue = [];
  }

  /**
   * Picks a held queue back up once warming is allowed again (the upload
   * finished, the tab came back). Cheap to call on every poll.
   */
  resume(): void {
    if (this.queue.length > 0 && !this.running && this.allowed()) void this.drain();
  }

  /** True while pages are still waiting to be fetched. */
  hasPending(): boolean {
    return this.queue.length > 0;
  }

  private async drain(): Promise<void> {
    if (this.running) return;
    this.running = true;
    try {
      while (this.queue.length > 0) {
        // Held, not dropped: resume() carries on once an upload finishes or
        // the tab is shown again. Data Saver and 2G will not lift, so then
        // the queue goes, and the page's poll can stop.
        if (!this.allowed()) {
          if (this.blockedForGood()) this.queue = [];
          break;
        }
        const page = this.queue.shift()!;
        if (this.done.has(page)) continue;
        this.done.add(page);
        const loaded = await page.preload?.(this.context());
        // One failure means a bad connection. A failed module stays failed in
        // the browser for the life of the tab, so each further fetch could
        // break another page's tap: stop warming in the background for this
        // visit. A finger on a link (warmNow) still fetches, since that is
        // the same fetch the tap makes anyway.
        if (loaded === false) {
          this.stopped = true;
          this.queue = [];
          break;
        }
      }
    } finally {
      this.running = false;
    }
  }
}
